import { CONFIG } from './config.js';
import { auth } from './auth.js';

/**
 * Wat we zeggen als de server een fout teruggeeft zonder melding.
 *
 * Alleen de terugval: heeft de server zelf iets te zeggen ('Datum monsters is verplicht'), dan
 * gaat dat voor -- dat is altijd concreter dan wat hier staat. Zonder deze tabel stond er
 * letterlijk 'HTTP 503: Service Unavailable' in beeld.
 */
export function httpFoutTekst(status) {
  if (status === 503 || status === 504) return 'De server is even niet bereikbaar. Probeer het over een paar minuten opnieuw.';
  if (status === 502) return 'De server gaf geen antwoord. Probeer het over een paar minuten opnieuw.';
  if (status >= 500) return 'Er ging iets mis op de server. Probeer het opnieuw; blijft het misgaan, geef het dan door.';
  if (status === 429) return 'Te veel verzoeken achter elkaar. Wacht even en probeer het opnieuw.';
  if (status === 413) return 'Het bestand is te groot.';
  if (status === 404) return 'Niet gevonden. Mogelijk is het inmiddels verwijderd.';
  if (status === 403) return 'Je hebt hier geen toegang.';
  return `Het verzoek is niet gelukt (${status}).`;
}

/**
 * API client for the MV Field Recorder backend
 */
export class APIClient {
  constructor() {
    this.baseURL = CONFIG.API_BASE;
    // this.baseURL = "http://0.0.0.0:8788/api";
  }

  /**
   * Make a request to the API
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    try {
      // Default headers
      const headers = {
        'Content-Type': 'application/json',
      };

      // Add auth token if available
      const token = auth.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }


      // Merge headers from options, allowing override
      if (options.headers) {
        Object.assign(headers, options.headers);
      }

      // Special handling for FormData: remove Content-Type to let browser set boundary
      if (options.body instanceof FormData) {
        delete headers['Content-Type'];
      }

      const fetchOptions = {
        ...options,
        headers
      };

      const response = await fetch(url, fetchOptions);

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        // Clear the expired token and trigger logout
        auth.logout();
        // Dispatch custom event to notify the app to show login screen
        window.dispatchEvent(new CustomEvent('auth-expired', {
          detail: { message: 'Uw sessie is verlopen. Log opnieuw in.' }
        }));
        throw new Error('Sessie verlopen. Log opnieuw in.');
      }

      // Handle different content types
      const contentType = response.headers.get('content-type');
      if (!response.ok) {
        let message = '';
        try {
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            message = data?.error || data?.message || '';
          } else {
            message = await response.text();
          }
        } catch {
          message = '';
        }
        // Geen melding van de server: dan zelf iets zeggen wat een gebruiker snapt. 'HTTP 503:
        // Service Unavailable' stond letterlijk in beeld, en daar kan niemand iets mee.
        throw new Error(message || httpFoutTekst(response.status));
      }

      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else if (contentType && (contentType.includes('application/xml') || contentType.includes('application/zip'))) {
        return response; // Return response object for XML/ZIP downloads
      } else {
        return await response.text();
      }
    } catch (error) {
      console.error('API request failed:', error);
      // fetch gooit een TypeError als er helemaal geen verbinding tot stand kwam -- offline, de
      // server ligt eruit, of hij is tijdens het ontwikkelen net aan het herstarten. De melding
      // die de browser dan geeft ('Failed to fetch') is voor een gebruiker niets waard.
      if (error instanceof TypeError) {
        throw new Error('Geen verbinding met de server. Controleer je internetverbinding en probeer het opnieuw.');
      }
      throw error;
    }
  }

  // --- Projects ---

  /**
   * List all projects for the current company.
   * @returns {Promise<Project[]>}
   */
  async getProjects() {
    const data = await this.request('/projects?limit=1000');
    return data.keys || [];
  }

  /**
   * Get a single project by ID (includes project_data if processed).
   * @param {string} projectId
   * @returns {Promise<Project>}
   */
  async getProject(projectId) {
    return await this.request(`/projects/${projectId}`);
  }

  /**
   * Create a new project.
   * @param {{ name: string, code?: string, parent_id?: string, instructions?: string }} projectData
   * @returns {Promise<Project>}
   */
  async createProject(projectData) {
    return await this.request('/projects', {
      method: 'POST',
      body: JSON.stringify(projectData)
    });
  }

  /**
   * Een TerraIndex-export (.ti) inlezen zonder iets op te slaan: levert het importlog op dat
   * de gebruiker te zien krijgt voordat hij besluit het project aan te maken.
   *
   * Het bestand gaat als rauwe body mee en niet als FormData — er is maar één bestand en geen
   * enkel ander veld, dus een multipart-envelop zou alleen maar iets zijn om weer uit te pakken.
   * @param {File} file
   */
  async previewTerraIndexImport(file) {
    return await this.request('/projects/import/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: file
    });
  }

  /**
   * Hetzelfde bestand, nu wél aanmaken: het project komt met alle boringen eronder terecht bij
   * het bedrijf waarin de gebruiker werkt.
   * @param {File} file
   */
  async createProjectFromTerraIndex(file) {
    return await this.request('/projects/import/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: file
    });
  }

  /**
   * Update an existing project.
   * @param {string} projectId
   * @param {{ name?: string, code?: string, parent_id?: string, instructions?: string }} projectData
   * @returns {Promise<Project>}
   */
  async updateProject(projectId, projectData) {
    return await this.request(`/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(projectData)
    });
  }

  /**
   * Delete a project and all its recordings.
   * @param {string} projectId
   */
  async deleteProject(projectId) {
    return await this.request(`/projects/${projectId}`, { method: 'DELETE' });
  }

  // --- Templates (sjablonen) ---
  async getTemplates() {
    const data = await this.request('/templates');
    return data.templates || [];
  }
  async getTemplate(templateId) {
    return await this.request(`/templates/${templateId}`);
  }
  async createTemplate(templateData) {
    return await this.request('/templates', {
      method: 'POST',
      body: JSON.stringify(templateData)
    });
  }
  async updateTemplate(templateId, templateData) {
    return await this.request(`/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(templateData)
    });
  }
  async deleteTemplate(templateId) {
    return await this.request(`/templates/${templateId}`, { method: 'DELETE' });
  }

  /**
   * List additional (non-recording) files attached to a project.
   * @param {string} projectId
   */
  async getProjectFiles(projectId) {
    return await this.request(`/projects/${projectId}/files`);
  }

  /**
   * Delete an additional file from a project.
   * @param {string} projectId
   * @param {string} fileName
   */
  async deleteFile(projectId, fileName) {
    return await this.request(`/projects/${projectId}/files/${fileName}`, { method: 'DELETE' });
  }

  /**
   * Upload additional files (audio, photos) to a project.
   * @param {string} projectId
   * @param {File[]} files
   */
  async uploadAdditionalFiles(projectId, files, parentRecordingId = null, domain = null, locatie = null) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    // Locatie van een in de backoffice ingesproken boring (browser-geolocatie), in dezelfde
    // velden als de veldapp: x = lengtegraad, y = breedtegraad. Geen locatie = niets meesturen.
    if (locatie && locatie.x != null && locatie.y != null) {
      formData.append('x', String(locatie.x));
      formData.append('y', String(locatie.y));
      if (locatie.altitude != null) formData.append('altitude', String(locatie.altitude));
      if (locatie.gpsAccuracy != null) formData.append('gpsAccuracy', String(locatie.gpsAccuracy));
      if (locatie.gpsVerticalAccuracy != null) formData.append('gpsVerticalAccuracy', String(locatie.gpsVerticalAccuracy));
      formData.append('gpsSource', 'device');
    }
    // Extra opname: attach this upload to an existing (primary) recording.
    if (parentRecordingId) formData.append('parentRecordingId', parentRecordingId);
    // Domein van de opname (lagen/peilbuis/watermonster/afwerking).
    if (domain) formData.append('domain', domain);

    // Het token gaat mee als Authorization-header, net als bij elke andere aanroep; `request()`
    // zet die zelf. Hier stond hij ook nog eens in de URL ("to avoid header stripping issues"),
    // en dat is precies hoe een sessietoken in serverlogs belandt. De backend accepteert een
    // token in de URL sinds 23-09 alleen nog bij een WebSocket of een GET op media.
    return await this.request(`/projects/${projectId}/upload`, {
      method: 'POST',
      body: formData
    });
  }

  /**
   * Open a WebSocket connection to the project event hub.
   * Returns the WebSocket instance. The caller should close it when done.
   *
   * Messages received: { type, recordingId?, status?, projectId }
   */
  connectWebSocket(projectId) {
    const token = auth.getToken();
    const base = this.baseURL
      .replace(/^https?/, match => (match === 'https' ? 'wss' : 'ws'))
      .replace(/\/api$/, '');
    const url = `${base}/api/projects/${projectId}/ws${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    return new WebSocket(url);
  }

  /**
   * Process project files (queue-based processing).
   * Triggers the job and returns immediately — callers should listen to
   * the project's WebSocket connection for completion events.
   */
  async processTranscriptions(projectId) {
    const response = await this.request(`/projects/${projectId}/process`, { method: 'POST' });
    if (response.status !== 'queued') throw new Error('Unexpected response format');
    return response;
  }

  /**
   * Download the SIKB0101 XML export for a project.
   * Returns the raw Response object for streaming/saving.
   * @param {string} projectId
   * @param {string} [queryParams=''] - optional query string e.g. "?include_monsters=1"
   * @returns {Promise<Response>}
   */
  async downloadXML(projectId, queryParams = '') {
    return await this.request(`/projects/${projectId}/xml${queryParams}`);
  }

  /**
   * Re-run the full processing pipeline (transcription + AI) for all recordings in a project.
   * Queues the job and returns immediately — listen to the WebSocket for completion.
   * @param {string} projectId
   */
  async reprocessProject(projectId) {
    const response = await this.request(`/projects/${projectId}/reprocess`, { method: 'POST' });
    if (response.status !== 'queued') throw new Error('Unexpected response format');
    return response;
  }

  /**
   * Re-run only the AI extraction step for all recordings in a project (skips transcription).
   * Queues the job and returns immediately — listen to the WebSocket for completion.
   * @param {string} projectId
   */
  async reprocessProjectAi(projectId) {
    const response = await this.request(`/projects/${projectId}/reprocess-ai`, { method: 'POST' });
    if (response.status !== 'queued') throw new Error('Unexpected response format');
    return response;
  }

  /**
   * Get all recordings for a project.
   * @param {string} projectId
   * @param {number} [page=1]
   * @param {number} [pageSize=0] - 0 means return all
   * @returns {Promise<Recording[]>}
   */
  async getProjectRecordings(projectId, page = 1, pageSize = 0) {
    const params = pageSize > 0
      ? `?page=${page}&pageSize=${pageSize}`
      : '';
    return await this.request(`/projects/${projectId}/recordings${params}`);
  }

  /**
   * Get a single recording by ID.
   * @param {string} recordingId
   * @returns {Promise<Recording>}
   */
  async getRecording(recordingId) {
    return await this.request(`/recordings/${recordingId}`);
  }

  /**
   * Queue transcription + AI extraction for a recording.
   * @param {string} recordingId
   */
  async processRecording(recordingId) {
    return await this.request(`/recordings/${recordingId}/process`, {
      method: 'POST'
    });
  }

  /**
   * Re-run transcription and AI extraction for a recording.
   * @param {string} recordingId
   */
  async reprocessRecording(recordingId, standard = null) {
    return await this.request(`/recordings/${recordingId}/reprocess`, {
      method: 'POST',
      body: JSON.stringify(standard ? { standard } : {}),
    });
  }

  /**
   * Overwrite a recording's transcription text.
   * The backend will automatically re-run AI extraction after saving.
   * @param {string} recordingId
   * @param {string} text
   */
  async updateRecordingTranscription(recordingId, text) {
    return await this.request(`/recordings/${recordingId}/transcription`, {
      method: 'PUT',
      body: JSON.stringify({ transcription_text: text })
    });
  }

  /**
   * Delete a recording and its audio file.
   * @param {string} recordingId
   */
  async deleteRecording(recordingId) {
    return await this.request(`/recordings/${recordingId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Save scan (jar barcode) data for a recording.
   * This only writes scan data — never touches result_data.
   * @param {string} recordingId
   * @param {object[]} jars
   */
  async updateRecordingScanData(recordingId, jars) {
    return await this.request(`/recordings/${recordingId}/scan-data`, {
      method: 'PUT',
      body: JSON.stringify({ jars })
    });
  }

  /**
   * Overwrite a recording's AI-extracted result_data.
   * The backend will re-run aggregateProjectData after saving.
   * @param {string} recordingId
   * @param {import('@mv/models').ResultData} data
   */
  async updateRecordingResult(recordingId, data) {
    return await this.request(`/recordings/${recordingId}/result`, {
      method: 'PUT',
      body: JSON.stringify({ result_data: data })
    });
  }

  /**
   * Sync orphaned audio files in R2 to the recordings table.
   * Creates missing DB rows for any files that were uploaded without an entry.
   * @param {string} projectId
   */
  async syncProjectRecordings(projectId) {
    return await this.request(`/projects/${projectId}/sync-recordings`, {
      method: 'POST'
    });
  }

  /**
   * Get all TerraIndex codelists (soil types, colours, BZB, etc.) from the backend.
   */
  async getTiCodelists() {
    return await this.request('/ti-codelists');
  }

  // --- Borings ---

  /**
   * Get all boreholes for a project (flat DB rows, not result_data).
   * @param {string} projectId
   * @returns {Promise<import('@mv/models').Boring[]>}
   */
  async getProjectBorings(projectId) {
    return await this.request(`/projects/${projectId}/borings`);
  }

  /**
   * Get boreholes linked to a specific recording.
   * @param {string} recordingId
   * @returns {Promise<import('@mv/models').Boring[]>}
   */
  async getRecordingBorings(recordingId) {
    return await this.request(`/recordings/${recordingId}/borings`);
  }

  /**
   * Create a new borehole under a project.
   * @param {string} projectId
   * @param {Partial<import('@mv/models').Boring>} data
   * @returns {Promise<import('@mv/models').Boring>}
   */
  /** Wie heeft toegang tot dit project (open model: iedereen, tenzij uitgesloten). */
  async getProjectTeam(projectId) {
    return await this.request(`/projects/${projectId}/team`);
  }

  /** Toegang tot dit project aan- of uitzetten voor één lid. */
  async setProjectTeamAccess(projectId, userId, hasAccess) {
    return await this.request(`/projects/${projectId}/team/${userId}`, {
      method: 'PUT', body: JSON.stringify({ hasAccess }),
    });
  }

  /**
   * Een gast uitnodigen voor één project: een opdrachtgever die meekijkt ('read') of een
   * onderaannemer die meewerkt ('write'). Alleen voor eigenaar/beheerder.
   * @returns {{ added?: boolean, invited?: boolean, token?: string, emailed: boolean }}
   */
  async inviteProjectGuest(projectId, { email, firstName, lastName, accessLevel }) {
    return await this._json(`/projects/${projectId}/guests`, {
      method: 'POST', body: JSON.stringify({ email, firstName, lastName, accessLevel }),
    });
  }

  /** Niveau van een gast op dit project wijzigen ('read'/'write'), of hem eraf halen (null). */
  async setProjectGuestAccess(projectId, userId, accessLevel) {
    return await this._json(`/projects/${projectId}/guests/${userId}`, {
      method: 'PUT', body: JSON.stringify({ accessLevel }),
    });
  }

  /** Als gast zelf uit een project stappen. */
  async leaveProjectAsGuest(projectId) {
    return await this._json(`/team/leave-project/${projectId}`, { method: 'DELETE' });
  }

  async createBoring(projectId, data) {
    return await this.request(`/projects/${projectId}/borings`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Dupliceer een boring: lagen, peilbuizen (zonder de metingen bij plaatsing), afwerking en
   * kopgegevens. De backend bepaalt wat er precies meegaat — zie BoringService.duplicate.
   * @param {string} boringId
   * @param {{ name: string, opmerking?: string|null, x?: number|null, y?: number|null, sourceName?: string }} data
   */
  /** Bulk bijwerken: `[{ boringId, x?, y?, z?, datum?, boormeester? }]`, alles of niets. */
  async bulkUpdateBorings(projectId, items) {
    return await this.request(`/projects/${projectId}/borings/bulk-update`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  async duplicateBoring(boringId, data) {
    return await this.request(`/borings/${boringId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update a borehole's metadata (name, type, coordinates, etc.).
   * @param {string} boringId
   * @param {Partial<import('@mv/models').Boring>} data
   * @returns {Promise<import('@mv/models').Boring>}
   */
  async updateBoring(boringId, data) {
    return await this.request(`/borings/${boringId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Save scan (jar barcode) data directly on a borehole.
   * @param {string} boringId
   * @param {object[]} jars
   */
  async updateBoringScanData(boringId, jars) {
    return await this.request(`/borings/${boringId}/scan-data`, {
      method: 'PUT',
      body: JSON.stringify({ jars })
    });
  }

  /**
   * Delete a borehole and all its layers.
   * @param {string} boringId
   */
  async deleteBoring(boringId) {
    return await this.request(`/borings/${boringId}`, { method: 'DELETE' });
  }

  // --- Layers ---

  /**
   * Create a new layer under a borehole.
   * @param {string} boringId
   * @param {Partial<import('@mv/models').Layer>} data
   * @returns {Promise<import('@mv/models').Layer>}
   */
  async createLayer(boringId, data) {
    return await this.request(`/borings/${boringId}/layers`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Update a layer.
   * @param {string} layerId
   * @param {Partial<import('@mv/models').Layer>} data
   * @returns {Promise<import('@mv/models').Layer>}
   */
  async updateLayer(layerId, data) {
    return await this.request(`/layers/${layerId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete a layer.
   * @param {string} layerId
   */
  async deleteLayer(layerId) {
    return await this.request(`/layers/${layerId}`, { method: 'DELETE' });
  }

  // --- Monsters ---

  /**
   * Get all samples for a project.
   * @param {string} projectId
   * @returns {Promise<import('@mv/models').Monster[]>}
   */
  async getProjectMonsters(projectId) {
    return await this.request(`/projects/${projectId}/monsters`);
  }

  /**
   * Create a new sample under a project.
   * @param {string} projectId
   * @param {Partial<import('@mv/models').Monster>} data
   * @returns {Promise<import('@mv/models').Monster>}
   */
  async createMonster(projectId, data) {
    return await this.request(`/projects/${projectId}/monsters`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Update a sample.
   * @param {string} monsterId
   * @param {Partial<import('@mv/models').Monster>} data
   * @returns {Promise<import('@mv/models').Monster>}
   */
  async updateMonster(monsterId, data) {
    return await this.request(`/monsters/${monsterId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete a sample.
   * @param {string} monsterId
   */
  async deleteMonster(monsterId) {
    return await this.request(`/monsters/${monsterId}`, { method: 'DELETE' });
  }

  // --- Peilbuizen ---

  /**
   * Get all piezometers for a project.
   * @param {string} projectId
   * @returns {Promise<import('@mv/models').Peilbuis[]>}
   */
  async getProjectPeilbuizen(projectId) {
    return await this.request(`/projects/${projectId}/peilbuizen`);
  }

  /**
   * Get piezometers linked to a specific borehole.
   * @param {string} boringId
   * @returns {Promise<import('@mv/models').Peilbuis[]>}
   */
  async getBoringPeilbuizen(boringId) {
    return await this.request(`/borings/${boringId}/peilbuizen`);
  }

  /**
   * Create a new piezometer under a project (not linked to a specific borehole).
   * @param {string} projectId
   * @param {Partial<import('@mv/models').Peilbuis>} data
   * @returns {Promise<import('@mv/models').Peilbuis>}
   */
  async createPeilbuis(projectId, data) {
    return await this.request(`/projects/${projectId}/peilbuizen`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Create a new piezometer linked to a specific borehole.
   * @param {string} boringId
   * @param {Partial<import('@mv/models').Peilbuis>} data
   * @returns {Promise<import('@mv/models').Peilbuis>}
   */
  async createBoringPeilbuis(boringId, data) {
    return await this.request(`/borings/${boringId}/peilbuizen`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Update a piezometer.
   * @param {string} peilbuisId
   * @param {Partial<import('@mv/models').Peilbuis>} data
   * @returns {Promise<import('@mv/models').Peilbuis>}
   */
  async updatePeilbuis(peilbuisId, data) {
    return await this.request(`/peilbuizen/${peilbuisId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete a piezometer.
   * @param {string} peilbuisId
   */
  async deletePeilbuis(peilbuisId) {
    return await this.request(`/peilbuizen/${peilbuisId}`, { method: 'DELETE' });
  }

  // --- Lab Koppeling v2 ---

  async getActiveLabConnections() {
    return await this.request('/lab-connections/active');
  }

  // Alle labkoppelingen in één keer, voor de bedrijvenlijst. Per bedrijf ophalen zou een
  // request per rij kosten.
  async getAdminLabConnections() {
    return await this.request('/admin/lab-connections');
  }

  async getAdminCompanyLabConnections(companyId) {
    return await this.request(`/admin/companies/${companyId}/lab-connections`);
  }

  async createAdminCompanyLabConnection(companyId, data) {
    return await this.request(`/admin/companies/${companyId}/lab-connections`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminLabConnection(labConnectionId, data) {
    return await this.request(`/admin/lab-connections/${labConnectionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async verifyAdminLabConnection(labConnectionId) {
    return await this.request(`/admin/lab-connections/${labConnectionId}/verify`, { method: 'POST' });
  }

  async syncAdminLabProducts(labConnectionId) {
    return await this.request(`/admin/lab-connections/${labConnectionId}/sync-products`, { method: 'POST' });
  }

  async deleteAdminLabConnection(labConnectionId) {
    return await this.request(`/admin/lab-connections/${labConnectionId}`, { method: 'DELETE' });
  }

  /** Alle labopdrachten van het bedrijf, voor de hoofdpagina Labopdrachten. */
  async getAllLabOrders() {
    return await this.request('/lab-orders');
  }

  /**
   * De stand van zaken bij het lab ophalen; geeft ook opdrachten buiten GeoVoice terug.
   *
   * `force` hoort bij het herlaadknopje: dat zet de pauze van vijf minuten aan de kant en valt
   * terug op tien seconden. Zonder die vlag antwoordt de backend binnen vijf minuten met wat er
   * al stond (`overgeslagen: true`).
   */
  async refreshLabOrderStatuses(days = 60, force = false) {
    return await this.request(`/lab-orders/statuses?days=${days}${force ? '&force=1' : ''}`, { method: 'POST' });
  }

  /** Het resultaatbestand van één opdracht bij het lab ophalen (op labordernummer). */
  async fetchLabOrderResults(labOrderNumber, opnieuw = false) {
    return await this.request(`/lab-orders/${labOrderNumber}/results${opnieuw ? '?opnieuw=1' : ''}`, { method: 'POST' });
  }

  /** Het aanleverbestand van een opdracht, als download. Verstuurt niets. */
  async downloadLabOrderXml(labOrderId) {
    const res = await fetch(`${this.baseURL}/lab-orders/${labOrderId}/order-xml`, {
      headers: { Authorization: `Bearer ${auth.getToken()}` },
    });
    if (!res.ok) {
      const msg = await res.json().catch(() => null);
      throw new Error(msg?.error || `Bestand ophalen mislukt (HTTP ${res.status})`);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const name = /filename="([^"]+)"/.exec(cd)?.[1] || 'labopdracht.xml';
    return { blob: await res.blob(), filename: name };
  }

  /** Het analysecertificaat (PDF) van een opdracht ophalen bij het lab. */
  async downloadLabOrderReport(labOrderNumber) {
    const res = await fetch(`${this.baseURL}/lab-orders/${labOrderNumber}/report`, {
      headers: { Authorization: `Bearer ${auth.getToken()}` },
    });
    if (!res.ok) {
      const msg = await res.json().catch(() => null);
      throw new Error(msg?.error || `Certificaat ophalen mislukt (HTTP ${res.status})`);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const name = /filename="([^"]+)"/.exec(cd)?.[1] || `analysecertificaat-${labOrderNumber}.pdf`;
    return { blob: await res.blob(), filename: name };
  }

  async getProjectLabOrders(projectId) {
    return await this.request(`/projects/${projectId}/lab-orders`);
  }

  async getProjectLabWorkbench(projectId) {
    return await this.request(`/projects/${projectId}/lab-workbench`);
  }

  async createLabOrderDraft(projectId, data = {}) {
    return await this.request(`/projects/${projectId}/lab-orders/draft`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getLabOrder(labOrderId) {
    return await this.request(`/lab-orders/${labOrderId}`);
  }

  /**
   * De labpakketten die bij deze koppeling en dit soort monster besteld mogen worden.
   *
   * De matrix en de certificaatvorm gaan mee omdat het lab daar verschillende pakketten aan
   * hangt: grond met AS3000-certificering kent een andere serie dan grond zonder. De vertaling
   * naar de monstersoortcode van het lab gebeurt op de server, zodat die codes hier niet
   * rondslingeren.
   */
  /** De productlijst (afspraken, urgenties, pakketten) nú opnieuw ophalen bij het lab. */
  async syncLabConnectionProducts(labConnectionId) {
    return await this.request(`/lab-connections/${labConnectionId}/sync-products`, { method: 'POST' });
  }

  /**
   * Pakketten van het lab. `group` (GRN, AG3, ...) kiest een groep zoals in ATMIS; leeg met een
   * zoekterm zoekt over alle groepen, en zonder `group` opent de groep die bij stap 1 past.
   */
  async getLabPackages(labConnectionId, { matrix, certificateFormat, query, category, limit, group } = {}) {
    const qs = new URLSearchParams({
      matrix: matrix || 'grond',
      certificateFormat: certificateFormat || '',
      q: query || '',
      category: category || '',
      limit: String(limit || 50),
    });
    if (group !== undefined) qs.set('group', group || '');
    return await this.request(`/lab-connections/${labConnectionId}/packages?${qs}`);
  }

  /** Wat er in een pakket zit (GetSuite bij het lab, bewaard op de server). */
  async getLabPackageInfo(labConnectionId, { code, sampleType }) {
    const qs = new URLSearchParams({ code, sampleType });
    return await this.request(`/lab-connections/${labConnectionId}/package-info?${qs}`);
  }

  /** Een labopdracht weggooien. Kan alleen zolang hij niet verstuurd is. */
  async deleteLabOrder(labOrderId) {
    return await this.request(`/lab-orders/${labOrderId}`, { method: 'DELETE' });
  }

  async updateLabOrderAdminData(labOrderId, data) {
    return await this.request(`/lab-orders/${labOrderId}/admin-data`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createLabAnalysisSample(labOrderId, data) {
    return await this.request(`/lab-orders/${labOrderId}/analysis-samples`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async bulkCreateLabAnalysisSamples(labOrderId, samples) {
    return await this.request(`/lab-orders/${labOrderId}/analysis-samples/bulk-create`, {
      method: 'POST',
      body: JSON.stringify({ samples }),
    });
  }

  async updateLabAnalysisSample(analysisSampleId, data) {
    return await this.request(`/lab-analysis-samples/${analysisSampleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /** Welke potjes in dit mengmonster gaan; de gekozen pakketten blijven staan. */
  async replaceLabAnalysisSampleParts(analysisSampleId, parts) {
    return await this.request(`/lab-analysis-samples/${analysisSampleId}/parts`, {
      method: 'PUT',
      body: JSON.stringify({ parts }),
    });
  }

  async deleteLabAnalysisSample(analysisSampleId) {
    return await this.request(`/lab-analysis-samples/${analysisSampleId}`, { method: 'DELETE' });
  }

  async validateLabOrder(labOrderId) {
    return await this.request(`/lab-orders/${labOrderId}/validate`, { method: 'POST' });
  }

  /**
   * De opdracht indienen bij het lab.
   *
   * Niet via `request`: een afkeuring komt terug als een 502 met een LIJST fouten van het
   * lab, en die wil je stuk voor stuk kunnen tonen. `request` maakt er één regel tekst van.
   */
  async sendLabOrder(labOrderId) {
    const res = await fetch(`${this.baseURL}/lab-orders/${labOrderId}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.getToken()}` },
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) return data;
    const fout = new Error(data?.error || 'Versturen mislukt');
    fout.errors = data?.errors || [];
    fout.missing = data?.missing || [];
    throw fout;
  }

  // --- Companies ---

  /**
   * Get all companies (Superadmin only).
   */
  async getCompanies() {
    return await this.request('/companies');
  }

  /**
   * Welke bedrijfsdatabases de Worker kent en welke nog vrij is (Superadmin only).
   * Komt uit de bindings van de draaiende Worker, dus dit is de echte capaciteit.
   */
  async getCompanyBindings() {
    return await this.request('/companies/bindings');
  }

  /**
   * Create a new company with initial admin (Superadmin only)
   */
  async createCompany(data) {
    return await this.request('/companies', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Update company name and/or db_binding (Superadmin only)
   */
  async updateCompany(companyId, data) {
    return await this.request(`/companies/${companyId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Toggle company active status (Superadmin only)
   */
  async updateCompanyStatus(companyId, isActive) {
    return await this.request(`/companies/${companyId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: isActive })
    });
  }

  // ── Admin (Sysadmin only) ──────────────────────────────────────────────

  /** All users with memberships, last login and Sysadmin flag. */
  async getAdminUsers() {
    const data = await this.request('/admin/users');
    return data.users || [];
  }
  async updateAdminUser(id, { firstName, lastName }) {
    return await this.request(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify({ firstName, lastName }) });
  }

  // ── Personal profile (own name + avatar) ──────────────────────────────────
  async getPersonalProfile() {
    return await this.request('/personal/profile');
  }
  async updatePersonalProfile({ firstName, lastName }) {
    return await this.request('/personal/profile', { method: 'PUT', body: JSON.stringify({ firstName, lastName }) });
  }
  /** Change own password (requires current password). */
  async changePassword(currentPassword, newPassword) {
    return await this._json('/personal/password', { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) });
  }
  /** Forgot-password: request a reset link (always generic — no account enumeration). */
  async forgotPassword(email) {
    return await this._json('/auth/forgot-password', { method: 'POST', public: true, body: JSON.stringify({ email }) });
  }
  /** Reset a password using the e-mailed token. */
  async resetPassword(token, newPassword) {
    return await this._json('/auth/reset-password', { method: 'POST', public: true, body: JSON.stringify({ token, newPassword }) });
  }
  async uploadAvatar(file) {
    const fd = new FormData();
    fd.append('file', file);
    return await this.request('/personal/avatar', { method: 'POST', body: fd });
  }
  async deleteAvatar() {
    return await this.request('/personal/avatar', { method: 'DELETE' });
  }
  /** Public avatar image URL for a user; pass avatarUpdatedAt to cache-bust. */
  avatarUrl(userId, avatarUpdatedAt) {
    if (!userId || !avatarUpdatedAt) return null;
    return `${this.baseURL}/personal/avatar/${userId}?v=${encodeURIComponent(avatarUpdatedAt)}`;
  }

  // ── Two-factor authentication (own account, session-scoped) ───────────────
  /** Current 2FA state: { enabled, required }. */
  async get2faStatus() {
    return await this._json('/auth/2fa/status');
  }
  /** Begin enrollment: { otpauthUri, secret }. */
  async setup2fa() {
    return await this._json('/auth/2fa/setup', { method: 'POST', body: JSON.stringify({}) });
  }
  /** Confirm a code and turn 2FA on. */
  async enable2fa(code) {
    return await this._json('/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) });
  }
  /** Turn 2FA off (blocked server-side for sysadmins). */
  async disable2fa(code) {
    return await this._json('/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ code }) });
  }
  /** Sysadmin: reset another user's 2FA so they can log in without it next time. */
  async resetUser2fa(userId) {
    return await this._json(`/admin/users/${userId}/2fa/reset`, { method: 'POST', body: JSON.stringify({}) });
  }

  /** Users that have access to a company (via memberships). */
  /** AI-kosten (schatting) per bedrijf: { companies: [{ id, totaal, aanroepen, onbekend }], prijzenVan }. */
  async getAdminAiCosts() {
    return this.request('/admin/ai-costs');
  }

  /** AI-kosten van één bedrijf per dag en functionaliteit: { dagen, categorieen, prijzenVan }. */
  async getAdminCompanyAiCosts(companyId) {
    return this.request(`/admin/companies/${companyId}/ai-costs`);
  }

  async getCompanyMembers(companyId) {
    const data = await this.request(`/admin/companies/${companyId}/members`);
    return data.members || [];
  }

  /** Toggle a user's Sysadmin flag. */
  async setUserSysadmin(userId, isSystemAdmin) {
    return await this.request(`/admin/users/${userId}/sysadmin`, {
      method: 'PUT',
      body: JSON.stringify({ is_system_admin: isSystemAdmin })
    });
  }

  /** Set a user's role in a specific company (manager|member). */
  async setUserMembershipRole(userId, companyId, role) {
    return await this._json(`/admin/users/${userId}/companies/${companyId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  /** Remove a user from a company (revoke access). */
  async removeUserMembership(userId, companyId) {
    return await this._json(`/admin/users/${userId}/companies/${companyId}`, { method: 'DELETE' });
  }

  // ── Analysis (Sysadmin only) — pipeline observability across all companies ──

  /** All pipeline jobs across companies (audio → transcript → boorprofiel). */
  async getAnalysisJobs(limit = 2000, { validation = false, since = null } = {}) {
    const q = `limit=${limit}${validation ? '&validation=1' : ''}${since ? `&since=${encodeURIComponent(since)}` : ''}`;
    return await this._json(`/admin/analysis/jobs?${q}`);
  }

  /** Full detail for one job: per-step input/output + timeline. Pass a run id to
   *  inspect a specific log entry's snapshot (else the recording's current state). */
  async getAnalysisJobDetail(companyId, recordingId, runId = null) {
    const q = runId != null ? `?run=${encodeURIComponent(runId)}` : '';
    return await this._json(`/admin/analysis/jobs/${companyId}/${recordingId}${q}`);
  }

  /**
   * Eén gescande pagina van een AI-aanroep, als object-URL voor een <img>. Met de gewone
   * Authorization-kop: een token in de URL is alleen voor audio en media toegestaan.
   */
  async analysisAiPage(companyId, jobId, n) {
    const res = await fetch(`${this.baseURL}/admin/analysis/jobs/${companyId}/${jobId}/pages/${n}`, {
      headers: { Authorization: `Bearer ${auth.getToken()}` },
    });
    if (!res.ok) throw new Error(`Pagina ${n} laden mislukt (HTTP ${res.status})`);
    return URL.createObjectURL(await res.blob());
  }

  /** Re-run AI extraction on a job's existing transcript (scoped to its company). */
  async reprocessAnalysisJob(companyId, recordingId) {
    return await this._json(`/admin/analysis/jobs/${companyId}/${recordingId}/reprocess`, { method: 'POST' });
  }

  /** Fetch a recording's audio as an authenticated object URL (for play/download). */
  /**
   * Directe URL naar de audio van een opname, bruikbaar als `src` van een <audio>.
   *
   * Bewust GEEN blob-URL meer. Die speelde prima af, maar het ingebouwde ⋮-menu van de browser
   * kent bij een blob geen bestandsnaam en bewaarde de download dan als een kale uuid zonder
   * extensie -- een bestand waar je niets mee kunt. Op een echte URL werkt datzelfde menu wel,
   * want de server stuurt `Content-Disposition` met de oorspronkelijke naam mee.
   *
   * De token in de queryreeks is hetzelfde patroon dat de opnamedetails al gebruiken: een
   * <audio>-element kan geen Authorization-kop meesturen.
   */
  analysisAudioUrl(companyId, recordingId) {
    const base = `${this.baseURL}/admin/analysis/jobs/${companyId}/${recordingId}/audio`;
    const token = auth.getToken();
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
  }

  /** Idem voor de validatieset. */
  validationAudioUrl(recordingId) {
    const base = `${this.baseURL}/admin/validation/samples/${recordingId}/audio`;
    const token = auth.getToken();
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
  }

  /**
   * Activiteitenlog van een project (+ deelprojecten) voor het projectoverzicht.
   *
   * `entity` filtert op de server. Filteren in de browser zou betekenen dat je uit de laatste
   * N regels de foto's zeeft in plaats van de laatste N foto's op te halen -- dan lijkt het
   * filter leeg terwijl er verderop genoeg staat.
   */
  async getProjectActivity(projectId, limit = 15, entity = '') {
    const q = entity ? `&entity=${encodeURIComponent(entity)}` : '';
    return await this.request(`/projects/${projectId}/activity?limit=${limit}${q}`);
  }

  // ── Foto's bij een boring ──────────────────────────────────────────────────

  async getBoringPhotos(boringId) {
    return await this.request(`/borings/${boringId}/photos`);
  }

  /**
   * Upload één foto. Omschrijving en koppeling zijn optioneel.
   * Geen Content-Type meesturen: die zet de browser zelf, inclusief de boundary.
   */
  async uploadBoringPhoto(boringId, file, { description, targetType, targetId, targetLabel } = {}) {
    const form = new FormData();
    form.append('file', file);
    if (description) form.append('description', description);
    if (targetType) {
      form.append('targetType', targetType);
      if (targetId) form.append('targetId', targetId);
      if (targetLabel) form.append('targetLabel', targetLabel);
    }
    return await this.request(`/borings/${boringId}/photos`, { method: 'POST', body: form });
  }

  async updateBoringPhoto(photoId, data) {
    return await this.request(`/photos/${photoId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteBoringPhoto(photoId) {
    return await this.request(`/photos/${photoId}`, { method: 'DELETE' });
  }

  // ── Deellocaties: vlakken op de projectkaart ──────────────────────────────

  async getSublocations(projectId) {
    return await this.request(`/projects/${projectId}/sublocations`);
  }

  /** @param {{ name: string, geometry: [number, number][], description?: string }} data */
  async createSublocation(projectId, data) {
    return await this.request(`/projects/${projectId}/sublocations`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateSublocation(sublocationId, data) {
    return await this.request(`/sublocations/${sublocationId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteSublocation(sublocationId) {
    return await this.request(`/sublocations/${sublocationId}`, { method: 'DELETE' });
  }

  /** De boringen van een deellocatie vastleggen: `boringIds` wordt de hele lijst. */
  async assignSublocationBorings(sublocationId, boringIds) {
    return await this.request(`/sublocations/${sublocationId}/borings`, { method: 'PUT', body: JSON.stringify({ boringIds }) });
  }

  /** De deellocaties van één boring: `sublocationIds` wordt de hele lijst (leeg = geen). */
  async setBoringSublocations(boringId, sublocationIds) {
    return await this.request(`/borings/${boringId}/sublocations`, { method: 'PUT', body: JSON.stringify({ sublocationIds }) });
  }

  // ── Rapportage (bèta) ─────────────────────────────────────────────────────

  async getReports(projectId) {
    return await this.request(`/projects/${projectId}/reports`);
  }

  /** @param {{ investigationTypes: string[], sublocationIds?: string[], title?: string }} data */
  async createReport(projectId, data) {
    return await this.request(`/projects/${projectId}/reports`, { method: 'POST', body: JSON.stringify(data) });
  }

  /** Een rapport verwijderen (zacht: het verdwijnt uit de lijst). */
  async deleteReport(reportId) {
    return this.request(`/reports/${reportId}`, { method: 'DELETE' });
  }

  async getReport(reportId) {
    return await this.request(`/reports/${reportId}`);
  }

  /** (Opnieuw) vullen uit de data; bewerkte en vastgezette blokken blijven staan. */
  async fillReport(reportId) {
    return await this.request(`/reports/${reportId}/fill`, { method: 'POST' });
  }

  /**
   * Het Word-bestand laten maken. `profiles`: [{ name: boringnaam, blob: PNG }] voor bijlage 2.
   * Geeft { fileKey, template, mode, unknownFields, images, bytes }.
   */
  async generateReport(reportId, profiles = []) {
    const form = new FormData();
    for (const p of profiles) form.append('profile', p.blob, `${p.name}.png`);
    return await this.request(`/reports/${reportId}/generate`, { method: 'POST', body: form });
  }

  /** Het laatst gemaakte Word-bestand, met de Authorization-header. */
  async downloadReport(reportId) {
    const res = await fetch(`${this.baseURL}/reports/${reportId}/download`, {
      headers: { Authorization: `Bearer ${auth.getToken()}` },
    });
    if (!res.ok) {
      const msg = await res.json().catch(() => null);
      throw new Error(msg?.error || `Rapport ophalen mislukt (HTTP ${res.status})`);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const name = /filename\*=UTF-8''([^;]+)/.exec(cd)?.[1];
    return { blob: await res.blob(), filename: name ? decodeURIComponent(name) : (/filename="([^"]+)"/.exec(cd)?.[1] || 'rapport.docx') };
  }

  // ── Rapportsjablonen van het bureau ───────────────────────────────────────
  // Een sjabloon = teksten + Word-opmaak. 'geovoice' is het ingebouwde sjabloon.

  /** { templates, knownMergeFields, canEdit } */
  async getReportTemplates() {
    return await this.request('/company/report-templates');
  }

  /** Nieuw sjabloon, of een kopie: { name?, subtitle?, copyFrom? } */
  async createReportTemplate(data) {
    return await this.request('/company/report-templates', { method: 'POST', body: JSON.stringify(data) });
  }

  /** { name?, subtitle?, isDefault? } → { templates } */
  async updateReportTemplate(id, data) {
    return await this.request(`/company/report-templates/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteReportTemplate(id) {
    return await this.request(`/company/report-templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Een (nieuw) Word-bestand voor dit sjabloon; geeft het sjabloon met de controle terug. */
  async uploadReportTemplateFile(id, file) {
    const form = new FormData();
    form.append('file', file);
    return await this.request(`/company/report-templates/${encodeURIComponent(id)}/file`, { method: 'POST', body: form });
  }

  /** Terug naar het standaardformat. */
  async deleteReportTemplateFile(id) {
    return await this.request(`/company/report-templates/${encodeURIComponent(id)}/file`, { method: 'DELETE' });
  }

  /** Het Word-bestand van een sjabloon (of het standaardformat): { blob, filename }. */
  /** Een voorbeeldrapport op dit sjabloon (verzonnen project), als Word-bestand. */
  async previewReportTemplate(id) {
    const res = await fetch(`${this.baseURL}/company/report-templates/${encodeURIComponent(id)}/preview`, {
      headers: { Authorization: `Bearer ${auth.getToken()}` },
    });
    if (!res.ok) {
      const msg = await res.json().catch(() => null);
      throw new Error(msg?.error || `Voorbeeld maken mislukt (HTTP ${res.status})`);
    }
    return { blob: await res.blob() };
  }

  async downloadReportTemplateFile(id) {
    const res = await fetch(`${this.baseURL}/company/report-templates/${encodeURIComponent(id)}/file`, {
      headers: { Authorization: `Bearer ${auth.getToken()}` },
    });
    if (!res.ok) {
      const msg = await res.json().catch(() => null);
      throw new Error(msg?.error || `Word-bestand ophalen mislukt (HTTP ${res.status})`);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const name = /filename\*=UTF-8''([^;]+)/.exec(cd)?.[1];
    return { blob: await res.blob(), filename: name ? decodeURIComponent(name) : (/filename="([^"]+)"/.exec(cd)?.[1] || 'sjabloon.docx') };
  }

  /** { texts, canEdit } — de teksten van dit sjabloon, met per tekst of hij is aangepast. */
  async getTemplateTexts(id) {
    return await this.request(`/company/report-templates/${encodeURIComponent(id)}/texts`);
  }

  /** @param {{ key: string, body: string, rule?: object|null }} data */
  async saveTemplateText(id, data) {
    return await this.request(`/company/report-templates/${encodeURIComponent(id)}/texts`, { method: 'PUT', body: JSON.stringify(data) });
  }

  /** Terug naar de standaardtekst. */
  async resetTemplateText(id, key) {
    return await this.request(`/company/report-templates/${encodeURIComponent(id)}/texts/${encodeURIComponent(key)}`, { method: 'DELETE' });
  }

  /** @param {{ content?: object, state?: string, action?: 'refresh' | 'keep' }} body */
  async updateReportBlock(blockId, body) {
    // Niet coderen: het id is '{rapport}:{paragraaf}:{soort}:{n}', alleen veilige tekens, en de
    // router decodeert het pad niet -- een %3A zou het id breken.
    return await this.request(`/report-blocks/${blockId}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  /**
   * URL van de afbeelding zelf. Die route staat achter auth, dus een gewone <img src> werkt
   * niet -- de browser stuurt daar geen token mee. De aanroeper haalt hem op als blob.
   */
  async fetchPhotoBlob(photoId) {
    const token = auth.getToken();
    const res = await fetch(`${this.baseURL}/photos/${photoId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Foto laden mislukt: HTTP ${res.status}`);
    return await res.blob();
  }

  // ── Projectkaart: geplande boorlocaties en tekeningen ──────────────────────

  async getPlannedPoints(projectId) {
    return await this.request(`/projects/${projectId}/planned-points`);
  }

  /**
   * Punten importeren (WGS84). `mode` 'merge' werkt bestaande namen bij en voegt nieuwe toe;
   * 'replace' gooit eerst alle punten van het project weg.
   */
  async importPlannedPoints(projectId, points, { mode = 'merge', sourceLayerId = null, sourceName = null } = {}) {
    return await this.request(`/projects/${projectId}/planned-points`, {
      method: 'POST', body: JSON.stringify({ points, mode, sourceLayerId, sourceName }),
    });
  }

  async updatePlannedPoint(pointId, data) {
    return await this.request(`/planned-points/${pointId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deletePlannedPoint(pointId) {
    return await this.request(`/planned-points/${pointId}`, { method: 'DELETE' });
  }

  async clearPlannedPoints(projectId) {
    return await this.request(`/projects/${projectId}/planned-points`, { method: 'DELETE' });
  }

  /**
   * DWG omzetten naar het tekeningmodel (zelfde vorm als parseDxf). Het bestand gaat als rauwe
   * body mee; de backend geeft het door aan de DWG-converter en bewaart het niet.
   */
  async convertDwg(projectId, file) {
    return await this.request(`/projects/${projectId}/dwg-convert`, {
      method: 'POST', body: file, headers: { 'Content-Type': 'application/octet-stream' },
    });
  }

  async getMapLayers(projectId) {
    return await this.request(`/projects/${projectId}/map-layers`);
  }

  /**
   * Tekening opslaan. `data` = GeoJSON-tekst (kind 'vector') of afbeelding-blob (kind 'image'),
   * `source` = het originele bestand (optioneel).
   */
  async createMapLayer(projectId, { meta, data, source }) {
    const form = new FormData();
    form.append('meta', JSON.stringify(meta));
    form.append('data', typeof data === 'string' ? new Blob([data], { type: 'application/geo+json' }) : data, 'data');
    if (source) form.append('source', source, source.name || 'bron');
    return await this.request(`/projects/${projectId}/map-layers`, { method: 'POST', body: form });
  }

  async updateMapLayer(layerId, data) {
    return await this.request(`/map-layers/${layerId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteMapLayer(layerId, { keepPoints = false } = {}) {
    return await this.request(`/map-layers/${layerId}${keepPoints ? '?keepPoints=1' : ''}`, { method: 'DELETE' });
  }

  /** Kaartbestand van een laag als blob (achter auth, net als foto's). */
  async fetchMapLayerBlob(layerId, { source = false } = {}) {
    const token = auth.getToken();
    const res = await fetch(`${this.baseURL}/map-layers/${layerId}/file${source ? '?source=1' : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Tekening laden mislukt: HTTP ${res.status}`);
    return await res.blob();
  }

  // ── Veldwerkopdracht ───────────────────────────────────────────────────────

  /** De geldende opdracht van een project: { fieldOrder, approved } (fieldOrder kan null zijn). */
  async getFieldOrder(projectId) {
    return await this.request(`/projects/${projectId}/field-order`);
  }

  /**
   * Formulier inlezen. `text` is het formulier als tekst (zie utils/veldwerkopdracht/
   * document-tekst.js); het origineel gaat mee om later terug te kunnen kijken.
   */
  /** Een gescand formulier laten uitlezen: `pages` zijn JPEG/PNG-data-URL's. Geeft { text }. */
  async ocrFieldOrder(projectId, pages, filename) {
    return await this.request(`/projects/${projectId}/field-orders/ocr`, { method: 'POST', body: JSON.stringify({ pages, filename }) });
  }

  async extractFieldOrder(projectId, file, text) {
    const form = new FormData();
    form.append('text', text);
    if (file) form.append('file', file, file.name);
    return await this.request(`/projects/${projectId}/field-orders`, { method: 'POST', body: form });
  }

  /** Zelf een opdracht opstellen: leeg concept, met projectnummer en -naam al ingevuld. */
  async createFieldOrder(projectId, data = {}) {
    return await this.request(`/projects/${projectId}/field-orders`, { method: 'POST', body: JSON.stringify({ data }) });
  }

  async updateFieldOrder(id, data) {
    return await this.request(`/field-orders/${id}`, { method: 'PUT', body: JSON.stringify({ data }) });
  }

  async approveFieldOrder(id) {
    return await this.request(`/field-orders/${id}/approve`, { method: 'POST' });
  }

  async deleteFieldOrder(id) {
    return await this.request(`/field-orders/${id}`, { method: 'DELETE' });
  }

  /** Het originele formulier als blob (achter auth). */
  // Bijlagen bij de veldwerkopdracht (per project; witte lijst in @mv/models attachmentTypes)
  async listFieldOrderAttachments(projectId) {
    return await this.request(`/projects/${projectId}/field-order-attachments`);
  }

  async uploadFieldOrderAttachment(projectId, file) {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return await this.request(`/projects/${projectId}/field-order-attachments`, { method: 'POST', body: fd });
  }

  async deleteFieldOrderAttachment(id) {
    return await this.request(`/field-order-attachments/${id}`, { method: 'DELETE' });
  }

  async fetchFieldOrderAttachment(id) {
    const token = auth.getToken();
    const res = await fetch(`${this.baseURL}/field-order-attachments/${id}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Bijlage laden mislukt: HTTP ${res.status}`);
    return await res.blob();
  }

  async fetchFieldOrderSource(id) {
    const token = auth.getToken();
    const res = await fetch(`${this.baseURL}/field-orders/${id}/source`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Bestand laden mislukt: HTTP ${res.status}`);
    return await res.blob();
  }

  /** Globale zoekbalk: één verzoek over alle domeinen. */
  async search(q) {
    return await this.request(`/search?q=${encodeURIComponent(q)}`);
  }

  // ── Persoonlijke UI-voorkeuren ─────────────────────────────────────────────
  // Serverseitig per gebruiker, zodat een kolomkeuze meegaat naar een andere browser
  // of tablet in plaats van aan één apparaat te hangen.

  async getPreferences() {
    return await this.request('/personal/preferences');
  }

  /** `value: null` zet de voorkeur terug op de standaard. */
  async setPreference(key, value) {
    return await this.request(`/personal/preferences/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  // ── Validation testset (AI regressietest, sysadmin) ────────────────────────
  // Elke sample hoort bij precies één extractieprofiel ("model"); de testset is
  // dus per model gescheiden — vandaar de profile-parameter op list/upload.
  async getValidationSamples(profile) {
    const q = profile ? `?profile=${encodeURIComponent(profile)}` : '';
    return await this.request(`/admin/validation/samples${q}`);
  }
  async uploadValidationFiles(files, parentRecordingId = null, profile = 'nen5104') {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    if (parentRecordingId) formData.append('parentRecordingId', parentRecordingId);
    formData.append('profile', profile);
    return await this.request('/admin/validation/upload', { method: 'POST', body: formData });
  }
  /** Verplaats een sample naar een ander model. */
  async setValidationProfile(id, profile) {
    return await this.request(`/admin/validation/samples/${id}/profile`, { method: 'POST', body: JSON.stringify({ profile }) });
  }
  /** mode: 'full' (transcribe + extract) | 'extract' (from stored transcript). */
  async processValidationSample(id, mode = 'full', aiConfig = null) {
    return await this.request(`/admin/validation/samples/${id}/process?mode=${mode}`, { method: 'POST', body: JSON.stringify(aiConfig || {}) });
  }
  /** Bevestig het huidige concept als het verwachte antwoord van deze sample. */
  async setValidationGolden(id) {
    return await this.request(`/admin/validation/samples/${id}/golden`, { method: 'POST', body: '{}' });
  }
  async setValidationCategory(id, category) {
    return await this.request(`/admin/validation/samples/${id}/category`, { method: 'POST', body: JSON.stringify({ category }) });
  }
  /** Volgorde (en categorie) van een reeks validatiesamples vastleggen. `ids` in de gewenste volgorde. */
  async reorderValidationSamples(category, ids) {
    return await this.request('/admin/validation/samples/reorder', { method: 'POST', body: JSON.stringify({ category, ids }) });
  }
  /** Vrije notitie bij een validatiesample. `null` wist hem. */
  async setValidationNote(id, note) {
    return await this.request(`/admin/validation/samples/${id}/note`, { method: 'POST', body: JSON.stringify({ note }) });
  }
  async clearValidationGolden(id) {
    return await this.request(`/admin/validation/samples/${id}/golden`, { method: 'DELETE' });
  }
  async getValidationBoorprofiel(id) {
    return await this.request(`/admin/validation/samples/${id}/boorprofiel`);
  }
  async testValidationSample(id, mode = 'extract', aiConfig = null) {
    return await this.request(`/admin/validation/samples/${id}/test?mode=${mode}`, { method: 'POST', body: JSON.stringify(aiConfig || {}) });
  }
  /**
   * Download één test als bestand. De naam komt uit de Content-Disposition van de server,
   * zodat het model en de bestandsnaam erin staan zonder dat de client die logica herhaalt.
   */
  async downloadValidationSample(id) {
    const res = await fetch(`${this.baseURL}/admin/validation/samples/${id}/export`, {
      headers: { Authorization: `Bearer ${auth.getToken()}` },
    });
    if (!res.ok) throw new Error(`Downloaden mislukt (HTTP ${res.status})`);
    const cd = res.headers.get('Content-Disposition') || '';
    const name = /filename="([^"]+)"/.exec(cd)?.[1] || `geovoice-test-${id}.json`;
    return { blob: await res.blob(), filename: name };
  }

  /** Importeer een eerder gedownloade test. Levert altijd een NIEUWE rij op. */
  async importValidationSample(json) {
    return await this.request('/admin/validation/import', { method: 'POST', body: JSON.stringify(json) });
  }

  /** Download een hele categorie als één bestand (alle samples, mét voorbeeld). */
  async downloadValidationCategory(profile, category) {
    const qs = new URLSearchParams({ profile, category: category ?? '' });
    const res = await fetch(`${this.baseURL}/admin/validation/categories/export?${qs}`, {
      headers: { Authorization: `Bearer ${auth.getToken()}` },
    });
    if (!res.ok) {
      const msg = await res.json().catch(() => null);
      throw new Error(msg?.error || `Downloaden mislukt (HTTP ${res.status})`);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const name = /filename="([^"]+)"/.exec(cd)?.[1] || 'geovoice-categorie.json';
    return { blob: await res.blob(), filename: name };
  }

  /** Zet een gedownloade categorie terug. Elk sample wordt een NIEUWE rij. */
  async importValidationCategory(json) {
    return await this.request('/admin/validation/categories/import', { method: 'POST', body: JSON.stringify(json) });
  }

  async deleteValidationSample(id) {
    return await this.request(`/admin/validation/samples/${id}`, { method: 'DELETE' });
  }
  async createValidationBoring(recordingId, name) {
    return await this.request(`/admin/validation/samples/${recordingId}/borings`, { method: 'POST', body: JSON.stringify({ name }) });
  }

  // ── Verplichte velden per norm ─────────────────────────────────────────────
  /** Sysadmin: de volledige lijst zoals hij nu geldt (regels + afwijkingen). */
  async getRequiredFieldsAdmin() { return await this.request('/admin/required-fields'); }
  /** Sysadmin: één regel aan/uit, met verplichte opmerking. */
  async setRequiredField(key, required, note) {
    return await this.request(`/admin/required-fields/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify({ required, note }) });
  }
  /** Iedereen: alleen de afwijkingen; samenvoegen met `resolveRequiredFields` uit @mv/models. */
  async getRequiredFieldOverrides() { return await this.request('/required-fields'); }

  // ── Model & prompt config (sysadmin) ───────────────────────────────────────
  async getModelOptions() { return await this.request('/admin/model-options'); }
  async getModelConfig() { return await this.request('/admin/model-config'); }
  async setModelConfig(config) { return await this.request('/admin/model-config', { method: 'PUT', body: JSON.stringify(config) }); }
  /** De prompts van Vooronderzoek en Flip AI (staan in de code; alleen lezen). */
  async getAiPrompts() { return await this.request('/admin/ai-prompts'); }
  /** Promptversies, optioneel gefilterd op één extractieprofiel ("model"). */
  async listPromptVersions(profile) {
    const q = profile ? `?profile=${encodeURIComponent(profile)}` : '';
    return await this.request(`/admin/prompt-versions${q}`);
  }
  async getPromptVersion(id) { return await this.request(`/admin/prompt-versions/${id}`); }
  async createPromptVersion(data) { return await this.request('/admin/prompt-versions', { method: 'POST', body: JSON.stringify(data) }); }
  async updatePromptVersion(id, data) { return await this.request(`/admin/prompt-versions/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deletePromptVersion(id) { return await this.request(`/admin/prompt-versions/${id}`, { method: 'DELETE' }); }
  // Boorprofiel editing on the validation DB — same shapes as the normal CRUD,
  // passed to <recording-row-detail> as its `crud` adapter.
  validationCrud = {
    updateBoring: (boringId, data) => this.request(`/admin/validation/borings/${boringId}`, { method: 'PUT', body: JSON.stringify(data) }),
    createLayer: (boringId, data) => this.request(`/admin/validation/borings/${boringId}/layers`, { method: 'POST', body: JSON.stringify(data) }),
    updateLayer: (layerId, data) => this.request(`/admin/validation/layers/${layerId}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteLayer: (layerId) => this.request(`/admin/validation/layers/${layerId}`, { method: 'DELETE' }),
    createBoringPeilbuis: (boringId, data) => this.request(`/admin/validation/borings/${boringId}/peilbuizen`, { method: 'POST', body: JSON.stringify(data) }),
    updatePeilbuis: (peilbuisId, data) => this.request(`/admin/validation/peilbuizen/${peilbuisId}`, { method: 'PUT', body: JSON.stringify(data) }),
    deletePeilbuis: (peilbuisId) => this.request(`/admin/validation/peilbuizen/${peilbuisId}`, { method: 'DELETE' }),
  };

  // Zichzelf toevoegen aan een bedrijf kan een systeembeheerder niet meer: dat gaf volledige
  // schrijfrechten en zette hem als vreemde tussen de bedrijfsleden. Meekijken loopt nu via de
  // schakelaar per bedrijf — zie updateCompany({ sysadmin_access }) en updateCompanyProfile.

  // ── Team & invitations ─────────────────────────────────────────────────
  // These surface the backend's error message (needed for invite/role flows).

  /**
   * Like request(), but parses and throws the server's `{ error }` message and
   * can skip the auth header for public endpoints (invitation accept page).
   */
  async _json(endpoint, options = {}) {
    const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
    const token = auth.getToken();
    if (token && !options.public) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${this.baseURL}${endpoint}`, { ...options, headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  }

  /** Alle activiteit van één teamlid, over alle projecten van het bedrijf. Manager+. */
  async getMemberActivity(userId, limit = 200) {
    return await this._json(`/team/members/${userId}/activity?limit=${limit}`);
  }

  /** Members of the active company + the caller's own role. */
  async getTeamMembers() {
    return await this._json('/team/members');
  }

  /** Pending invitations for the active company. */
  async getTeamInvitations() {
    const data = await this._json('/team/invitations');
    return data.invitations || [];
  }

  /** Invite someone (role capped server-side). Returns { added } or { invited, token }. */
  /** `extra` alleen bij rol 'guest': { projectId, accessLevel } — een gast hoort bij één project. */
  async inviteTeamMember(email, role, firstName, lastName, extra = {}) {
    return await this._json('/team/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role, firstName, lastName, ...extra }),
    });
  }

  /**
   * De acceptatielink van één uitnodiging, voor de kopieerknop.
   *
   * De lijst geeft het token niet meer mee: dat token ís de sleutel tot het account, en dat
   * hoort niet in een respons die iedereen met de teampagina open krijgt. Nu wordt hij per
   * uitnodiging opgehaald, op het moment dat iemand op 'Kopieer link' drukt.
   */
  async getInvitationLink(invitationId) {
    const data = await this._json(`/team/invitations/${invitationId}/link`);
    return data.token;
  }

  async revokeInvitation(invitationId) {
    return await this._json(`/team/invitations/${invitationId}`, { method: 'DELETE' });
  }

  async changeMemberRole(userId, role) {
    return await this._json(`/team/members/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async removeMember(userId) {
    return await this._json(`/team/members/${userId}`, { method: 'DELETE' });
  }

  async transferOwnership(userId) {
    return await this._json('/team/transfer-ownership', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  /** Project access for a member: { projects:[{id,name,code,hasAccess}], restrictable, role }. */
  async getMemberProjectAccess(userId) {
    return await this._json(`/team/members/${userId}/project-access`);
  }

  /** Replace a member's denied-project set. */
  /**
   * Projecttoegang van één lid vervangen. Bij een lid: `{ deniedProjectIds }` (opt-out). Bij een
   * gast: `{ guestAccess: [{ projectId, accessLevel }] }` (opt-in). De aanroeper geeft het
   * juiste veld mee; de backend weet de rol.
   */
  async setMemberProjectAccess(userId, payload) {
    const body = Array.isArray(payload) ? { deniedProjectIds: payload } : payload;
    return await this._json(`/team/members/${userId}/project-access`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  // ── Invitation accept (public) ─────────────────────────────────────────

  /** Public: what the invite is for (company, e-mail, role, validity, user_exists). */
  async getInvitation(token) {
    return await this._json(`/invitations/${encodeURIComponent(token)}`, { public: true });
  }

  /** Public: accept an invite. password only required for a new account. */
  async acceptInvitation(token, password) {
    return await this._json('/invitations/accept', {
      method: 'POST',
      public: true,
      body: JSON.stringify({ token, password }),
    });
  }

  // --- Recording instructions (Recorder App settings) ---

  /** Per-company recording instructions voor een NEN-standaard. Returns { instructions, isDefault, standard, canEdit }. */
  async getRecordingInstructions(profile = 'nen6693') {
    return await this.request(`/recording-instructions?profile=${encodeURIComponent(profile)}`);
  }

  /** Save the instructions HTML voor één extractiemodel (owner/manager/sysadmin only — enforced server-side). */
  async updateRecordingInstructions(profile, instructions) {
    return await this.request('/recording-instructions', {
      method: 'PUT',
      body: JSON.stringify({ profile, instructions }),
    });
  }

  /** Boorprofiel-texturen van het bedrijf: { overrides, canEdit }. Alleen afwijkingen van de norm. */
  async getCompanyTextures() {
    return await this.request('/company-textures');
  }

  /** Vervang het voorbeeldprofiel van /settings/textures voor het actieve bedrijf; null = standaardvoorbeeld. */
  async updateTextureSample(sample) {
    return await this.request('/company-textures/sample', {
      method: 'PUT',
      body: JSON.stringify({ sample }),
    });
  }

  /** Vervang alle textuur-afwijkingen van het bedrijf (owner/manager/sysadmin — server-side gecontroleerd). */
  async updateCompanyTextures(overrides) {
    return await this.request('/company-textures', {
      method: 'PUT',
      body: JSON.stringify({ overrides }),
    });
  }

  /**
   * Mengmonsterassistent: een vraag van de adviseur → { action, filter, answer }.
   * `options` zijn de namen die in het lagenoverzicht voorkomen; het model kiest daaruit.
   * `ids` ({ labOrderId, projectId }) staan alleen in het logboek van /admin/analysis.
   */
  async askLabAssistant(question, filter, options, context, ids = {}) {
    return await this.request('/lab-assistant', {
      method: 'POST',
      body: JSON.stringify({ question, filter, options, context, labOrderId: ids.labOrderId, projectId: ids.projectId }),
    });
  }

  /** Reset een standaard terug naar de systeem-default. Returns { instructions, isDefault, standard }. */
  async resetRecordingInstructions(profile) {
    return await this.request('/recording-instructions/reset', {
      method: 'POST',
      body: JSON.stringify({ profile }),
    });
  }

  /** Bedrijfsprofiel van de actieve company. Returns { name, logo, canEdit }. */
  async getCompanyProfile() {
    return await this.request('/company/profile');
  }

  // ── Scangeluiden van het bedrijf (veldapp: geluid bij een gelukte barcodescan) ──
  async getCompanySounds() {
    return (await this.request('/company/sounds'))?.sounds ?? [];
  }

  /** Eén geluid toevoegen. De lengte meet de browser (zie scan-sounds-card); max 3 s, 600 KB. */
  async uploadCompanySound(file, name, durationMs) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name);
    fd.append('durationMs', String(Math.round(durationMs)));
    return await this.request('/company/sounds', { method: 'POST', body: fd });
  }

  /** Alleen de naam wijzigen; het geluid en zijn id blijven hetzelfde. */
  async renameCompanySound(id, name) {
    return await this.request(`/company/sounds/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
  }

  async deleteCompanySound(id) {
    return await this.request(`/company/sounds/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Het geluid zelf, als blob: de route staat achter auth, een kale <audio src> stuurt geen token mee. */
  async fetchCompanySoundBlob(id) {
    const token = auth.getToken();
    const res = await fetch(`${this.baseURL}/company/sounds/${encodeURIComponent(id)}/audio`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Geluid laden mislukt: HTTP ${res.status}`);
    return await res.blob();
  }

  /** Sla het bedrijfslogo op (base64 data-URI, of null om te wissen). */
  async updateCompanyLogo(logo) {
    return await this.request('/company/profile', {
      method: 'PUT',
      body: JSON.stringify({ logo }),
    });
  }

  /**
   * Zet meekijken door systeembeheerders aan of uit voor het actieve bedrijf.
   *
   * Hetzelfde endpoint als het logo, maar een apart veld: de backend raakt de schakelaar alleen
   * aan als hij expliciet is meegestuurd, zodat een logo-opslag hem nooit per ongeluk omzet.
   */
  async setCompanySysadminAccess(enabled) {
    return await this.request('/company/profile', {
      method: 'PUT',
      body: JSON.stringify({ sysadminAccess: !!enabled }),
    });
  }

  /** Sysadmin: read the system-default instructions voor één extractiemodel. */
  async getDefaultRecordingInstructions(profile = 'nen6693') {
    return await this.request(`/admin/recording-instructions/default?profile=${encodeURIComponent(profile)}`);
  }

  /** Sysadmin: save the system-default instructions voor één extractiemodel. */
  async updateDefaultRecordingInstructions(profile, instructions) {
    return await this.request('/admin/recording-instructions/default', {
      method: 'PUT',
      body: JSON.stringify({ profile, instructions }),
    });
  }
}

// Export a singleton instance
export const api = new APIClient();
